import { useCallback, useEffect, useState } from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import type { AccessorFnColumnDef, SortingState } from '@tanstack/react-table';
import { Data, DATA_SOURCE, DATA_TYPE } from 'utils/types';
import Table, { Cell } from 'ui/components/common/Table';
import { Button, DeleteItemModal } from 'ui/components/common';
import { compact, debounce } from 'lodash';
import { Searchbox, FilterForm } from 'ui/components/form';
import { PagePlaceholder } from 'ui/partials';
import { useSearchParams } from 'react-router-dom';
import type { URLSearchParamsInit } from 'react-router-dom';
import { cn } from 'utils/helpers';
import { CheckIcon } from 'ui/components/icon';
import useModal from 'ui/partials/Modal/useModal';
import {
  Can,
  useCan as useCanRolePermissions,
} from 'utils/providers/RolePermission';
import { ACTIONS, PERMISSION_KEYS } from 'utils/constants';
import { NavOption } from 'ui/components/nav/NavAction/NavAction';
import Pagination, { PaginationWrapper } from 'ui/components/nav/Pagination';
import { useQueryParams } from 'hooks';

import { useDeleteData, useGetDatas } from '../../hooks';
import { useDataManagementContext } from '.';

const FILTER_INIT: URLSearchParamsInit = {
  sortBy: 'createdAt',
  orderBy: 'ASC',
  page: '1',
  perPage: '12',
  keyword: '',
};

const Fields = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [query, setQuery] = useQueryParams();

  const handleFilterOnChange = (rest: { [key: string]: any }) => {
    setQuery(rest);
  };

  const { data: fieldsData } = useGetDatas({
    ...query,
  });
  const [params] = useSearchParams();
  const action = params.get('action');
  const { mutate: deleteField, isLoading: singleDeleteLoading } =
    useDeleteData();
  const canEditData = useCanRolePermissions(
    ACTIONS.UPDATE,
    PERMISSION_KEYS.DATA,
  );
  const canDeleteData = useCanRolePermissions(
    ACTIONS.DELETE,
    PERMISSION_KEYS.DATA,
  );
  const { showModal, hideModal } = useModal();

  const { setIsOpen, setSelectedFieldId } = useDataManagementContext();
  const typeMap = {
    [DATA_TYPE.STRING]: 'Text',
    [DATA_TYPE.MOBILENUMBER]: 'Phone Number',
    [DATA_TYPE.NUMBER]: 'Number',
    [DATA_TYPE.DATE]: 'Date Picker',
    [DATA_TYPE.SELECT]: 'Select Dropdown',
    [DATA_TYPE.MULTISELECT]: 'Multi-select Dropdown',
    [DATA_TYPE.CHECKBOX]: 'Checkbox',
    [DATA_TYPE.RADIOBOX]: 'Radio',
    [DATA_TYPE.BOOLEAN]: 'Toggle',
  };

  const sourceMap = {
    [DATA_SOURCE.COMPANY]: 'Companies',
    [DATA_SOURCE.LEAD]: 'Leads',
    [DATA_SOURCE.DEAL]: 'Deals',
    [DATA_SOURCE.TASK]: 'Tasks',
  };

  const columnHelper = createColumnHelper<Data>();

  const columns = (onClick?: (id: string) => void) =>
    [
      columnHelper.accessor('name', {
        header: 'NAME',
        cell: (info) => (
          <Cell
            onClick={() => onClick?.(info.cell.row.id)}
            data={{ label: info.getValue() }}
          />
        ),
      }),
      columnHelper.accessor('type', {
        header: 'Field Type',
        cell: (info) => (
          <Cell
            onClick={() => onClick?.(info.cell.row.id)}
            data={{ label: typeMap[info.getValue()] }}
          />
        ),
      }),
      columnHelper.accessor('source', {
        header: 'Assigned Module',
        cell: (info) => (
          <Cell
            onClick={() => onClick?.(info.cell.row.id)}
            data={{ label: sourceMap[info.getValue()] }}
          />
        ),
      }),
      columnHelper.accessor('totalAssociatedId', {
        header: 'In Use',
        enableSorting: false,
        cell: (info) => (
          <div className="flex items-center justify-center">
            {!!info.getValue() && (
              <span
                className={cn(
                  'h-5 w-5 rounded-full',
                  'flex items-center justify-center',
                  'bg-primary-base',
                )}
              >
                <CheckIcon className="h-[10px] w-[10px] text-fill-tertiary" />
              </span>
            )}
          </div>
        ),
      }),
      columnHelper.accessor('createdAt', {
        header: 'DATE ADDED',
        cell: (info) => (
          <Cell
            onClick={() => onClick?.(info.cell.row.id)}
            data={{ label: info.getValue() }}
          />
        ),
      }),
    ] as AccessorFnColumnDef<Data>[];

  const handleShowCreateField = useCallback(() => {
    setIsOpen?.(true);
  }, [setIsOpen]);

  const actions = useCallback(
    (data: any) => {
      const acts = compact<NavOption>([
        canEditData && {
          key: 'Edit',
          text: 'Edit',
          onClick: () => {
            setSelectedFieldId?.(data._id);
          },
        },
        canDeleteData && {
          key: 'Delete',
          text: 'Delete',
          ...(data.totalAssociatedId.length && {
            disabled: true,
          }),
          onClick: () =>
            showModal({
              content: (
                <DeleteItemModal
                  text="Field"
                  onClickDelete={() =>
                    deleteField(data._id as string, {
                      onSuccess: hideModal,
                    })
                  }
                  isLoading={singleDeleteLoading}
                  isDeletable={data.isDeletable}
                  validation
                />
              ),
              title: 'Delete Field',
            }),
        },
      ]);

      return acts.length ? acts : [];
    },
    [
      canDeleteData,
      canEditData,
      deleteField,
      hideModal,
      setSelectedFieldId,
      showModal,
      singleDeleteLoading,
    ],
  );

  useEffect(() => {
    if (action === 'create') {
      handleShowCreateField();
    }
  }, [action, handleShowCreateField]);

  return (
    <>
      <h4 className="font-semibold mb-[18px]">Custom Fields</h4>
      <FilterForm initialValues={query} onChange={handleFilterOnChange}>
        {({ values, handleChange }) => (
          <div className="bg-fill-tertiary rounded-md">
            <div className="flex px-[22px] py-4 justify-between items-center">
              <Searchbox
                placeholder="Search field"
                className="w-[290px]"
                onChange={debounce(
                  (event: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange('keyword', event.target.value);
                  },
                  300,
                )}
              />
              <Can I={ACTIONS.CREATE} this={PERMISSION_KEYS.DATA}>
                <Button onClick={handleShowCreateField} label="ADD FIELD" />
              </Can>
            </div>
            {!fieldsData?.data?.length ? (
              <PagePlaceholder
                className="py-5"
                iconType="no-data"
                title="No record yet"
                backButtonText="ADD FIELD"
                onClick={handleShowCreateField}
                description="There seems to be no records available to show."
              />
            ) : (
              <Table<Data>
                columns={columns()}
                actions={actions}
                serverSideSorting
                sorting={sorting}
                setSorting={(val: any) => {
                  handleChange('sortBy', val()[0].id);
                  handleChange('orderBy', val()[0].desc ? 'DESC' : 'ASC');
                  setSorting(val());
                }}
                data={fieldsData.data}
              />
            )}
            <PaginationWrapper
              hasActions
              handlePerPage={(_perPage) => handleChange('perPage', _perPage)}
              perPage={values.perPage || FILTER_INIT.perPage}
            >
              <Pagination
                page={parseInt(values.page || FILTER_INIT.page, 10)}
                totalPages={fieldsData?.meta?.totalPages}
                onClick={(_page) => handleChange('page', _page)}
              />
            </PaginationWrapper>
          </div>
        )}
      </FilterForm>
    </>
  );
};

export default Fields;
